(() => {
  const namespace = "GuruCollection";
  if (window[namespace]) return;

  const rootSelector = "[data-guru-collection-root]";
  const desktopQuery = window.matchMedia("(min-width: 1024px)");
  const storyMobileQuery = window.matchMedia("(max-width: 600px)");
  const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const previewTimers = new WeakMap();
  const previewRequests = new WeakMap();
  const previewGenerations = new WeakMap();
  const searchTimers = new WeakMap();
  const searchRequests = new WeakMap();
  const searchPausedForDraft = new WeakSet();
  const busyOwners = new WeakMap();
  const infiniteLoadingStates = new WeakMap();
  const initializedVideos = new WeakSet();
  const videoCleanups = new WeakMap();
  let collectionRequest = null;
  let failedNavigation = null;
  let failedSearch = null;
  const fallbackTranslations = Object.freeze({
    product: "product",
    products: "products",
    removeFilter: "Remove filter __LABEL__",
    filters: "Filters",
    hideFilters: "Hide filters",
    showCount: "Show __COUNT__ __NOUN__",
    showProducts: "Show products",
    zeroMatches: "No products match",
    updating: "Updating …",
    updateFailed: "Could not update the products.",
    searchLengthError: "Enter between 2 and 80 characters to search the collection.",
    searchLengthStatus: "The search must be between 2 and 80 characters.",
    searchUnavailable: "Search in this collection is temporarily unavailable. Use the filters in the meantime.",
    searching: "Searching the collection …",
    searchFailed: "The search could not be completed.",
    searchFailedStatus: "The search failed.",
    emptySearchCopy: "Try a broader search or clear your selections to see more products.",
    viewAllProducts: "View all products",
    loadMore: "Load more products",
    loadingMore: "Loading more products …",
    loadedMore: "__COUNT__ more loaded.",
    loadMoreRetry: "Try to load more products",
    loadMoreFailed: "More products could not be loaded automatically.",
    productsUpdateFailedStatus: "The products could not be updated. Try again.",
    paginationLabel: "Pagination navigation",
    goToPage: "Go to page __PAGE__",
  });
  const catalogControls = window.GuruCatalogControls?.create({
    rootSelector,
    surface: "collection",
    i18nSelector: "[data-guru-collection-i18n]",
    fallbackTranslations,
  });
  if (!catalogControls) return;
  const {
    dialogFor,
    formFor,
    isNonNegativeSafeInteger,
    isPositiveSafeInteger,
    localizedCopy,
    matchingFacetInput,
    parseSection,
    productNoun,
    resetFacetEvidence,
    rootFor,
    sectionFor,
    setBusy,
    syncQuickFilters,
  } = catalogControls;
  const createSvgIcon = (name) => {
    if (window.GuruSvgIcons?.create) return window.GuruSvgIcons.create(name);
    const paths = {
      close: "m6 6 12 12M18 6 6 18",
      "chevron-left": "m15 5-7 7 7 7",
      "chevron-right": "m9 5 7 7-7 7",
    };
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    svg.setAttribute("class", `guru-svg-icon guru-svg-icon--${name}`);
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("focusable", "false");
    path.setAttribute("d", paths[name] || "");
    svg.append(path);
    return svg;
  };

  const cleanUrl = (value) => {
    const url = new URL(value, window.location.href);
    url.searchParams.delete("section_id");
    return url;
  };

  const localSearchParam = (root) => root?.dataset.guruCollectionSearchQueryParam || "q";
  const localSearchQuery = (root, value = window.location.href) => (
    cleanUrl(value).searchParams.get(localSearchParam(root))?.trim() || ""
  );
  const searchScope = (root) => ({
    kind: root?.dataset.guruCollectionSearchScopeKind || "collection",
    value: root?.dataset.guruCollectionSearchScopeValue || root?.dataset.guruCollectionHandle || "",
  });

  const authoritativeSearchUrl = (root, value) => {
    const url = cleanUrl(value);
    if (!url.searchParams.has("sort_by")) {
      const defaultSort = root?.dataset.guruCollectionDefaultSort?.trim();
      if (defaultSort) url.searchParams.set("sort_by", defaultSort);
    }
    return url;
  };

  const normalizedPathname = (pathname) => pathname === "/" ? pathname : pathname.replace(/\/+$/, "");

  const expectedCollectionPath = (root) => {
    const clearUrl = cleanUrl(root?.dataset.guruCollectionClearUrl || "");
    if (clearUrl.origin !== window.location.origin) return null;
    const pathname = normalizedPathname(clearUrl.pathname);
    const segments = pathname.split("/").filter(Boolean);
    const handle = root?.dataset.guruCollectionHandle || "";
    if (segments.length < 2 || segments.at(-2) !== "collections") return null;
    try {
      if (decodeURIComponent(segments.at(-1)) !== handle) return null;
    } catch (_error) {
      return null;
    }
    return pathname;
  };

  const isCanonicalCollectionUrl = (root, url) => {
    const expectedPath = expectedCollectionPath(root);
    return Boolean(
      expectedPath &&
      url.origin === window.location.origin &&
      url.username === "" &&
      url.password === "" &&
      url.hash === "" &&
      normalizedPathname(url.pathname) === expectedPath
    );
  };

  const sortedSearchEntries = (url, withoutPage = false) => Array.from(url.searchParams.entries())
    .filter(([name]) => !withoutPage || name !== "page")
    .sort(([leftName, leftValue], [rightName, rightValue]) => (
      leftName.localeCompare(rightName) || leftValue.localeCompare(rightValue)
    ))
    .map(([name, value]) => `${name}\u0000${value}`);

  const sortedCollectionStateEntries = (root, url) => Array.from(url.searchParams.entries())
    .filter(([name]) => name !== "page" && name !== localSearchParam(root) && name !== "section_id")
    .sort(([leftName, leftValue], [rightName, rightValue]) => (
      leftName.localeCompare(rightName) || leftValue.localeCompare(rightValue)
    ))
    .map(([name, value]) => `${name}\u0000${value}`);

  const isExpectedPageUrl = (root, value, canonicalUrl, page) => {
    if (typeof value !== "string" || value === "") return false;
    const url = cleanUrl(value);
    if (!isCanonicalCollectionUrl(root, url)) return false;
    if (sortedSearchEntries(url, true).join("\u0001") !== sortedSearchEntries(canonicalUrl, true).join("\u0001")) return false;
    const pageValues = url.searchParams.getAll("page");
    if (page === 1) return pageValues.length === 0;
    return pageValues.length === 1 && pageValues[0] === `${page}`;
  };

  const urlFromForm = (form) => {
    const root = rootFor(form);
    const url = new URL(form.action || window.location.href, window.location.href);
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (`${value}`.trim() !== "") params.append(key, value);
    });
    const queryParam = localSearchParam(root);
    const currentQuery = localSearchQuery(root);
    if (currentQuery) params.set(queryParam, currentQuery);
    const scope = searchScope(root);
    if ((scope.kind === "vendor" || scope.kind === "type") && scope.value) {
      params.set("q", scope.value);
    }
    params.delete("page");
    url.search = params.toString();
    return url;
  };

  const setLiveMessage = (root, message) => {
    const live = root?.querySelector("[data-guru-collection-live]");
    if (live) live.textContent = message;
  };

  const setError = (root, message = "", retryable = true) => {
    const error = root?.querySelector("[data-guru-collection-error]");
    if (!error) return;
    error.hidden = !message;
    const copy = error.querySelector("[data-guru-collection-error-copy]");
    if (copy) copy.textContent = message;
    const retry = error.querySelector("[data-guru-collection-retry]");
    if (retry) retry.hidden = !message || !retryable;
  };

  const claimBusy = (root, owner) => {
    if (!root || !owner) return;
    busyOwners.set(root, owner);
    setBusy(root, true);
  };

  const releaseBusy = (root, owner) => {
    if (!root || busyOwners.get(root) !== owner) return false;
    busyOwners.delete(root);
    setBusy(root, false);
    return true;
  };

  const cancelSearch = (root, form) => {
    const owner = form ? searchRequests.get(form) : null;
    owner?.abort();
    if (form && searchRequests.get(form) === owner) {
      searchRequests.delete(form);
      form.removeAttribute("aria-busy");
    }
    if (owner) releaseBusy(root, owner);
  };

  const scrollCollectionGridToStart = (root) => {
    const grid = root?.querySelector("[data-guru-collection-grid]");
    if (!grid) return false;
    const toolbar = root.querySelector(".guru-collection__toolbar");
    const toolbarTop = Number.parseFloat(toolbar ? window.getComputedStyle(toolbar).top : "0");
    const stickyOffset = (Number.isFinite(toolbarTop) ? Math.max(0, toolbarTop) : 0) +
      (toolbar?.getBoundingClientRect().height || 0) + 12;
    const top = Math.max(0, Math.round(window.scrollY + grid.getBoundingClientRect().top - stickyOffset));
    window.scrollTo({ top, behavior: "auto" });
    return true;
  };

  const stickySearchIsAboveScrolledResults = (root) => {
    const toolbar = root?.querySelector(".guru-collection__toolbar");
    const grid = root?.querySelector("[data-guru-collection-grid]");
    if (!toolbar || !grid) return false;
    const styles = window.getComputedStyle(toolbar);
    const stickyTop = Number.parseFloat(styles.top);
    if (styles.position !== "sticky" || !Number.isFinite(stickyTop)) return false;
    const toolbarRect = toolbar.getBoundingClientRect();
    const gridRect = grid.getBoundingClientRect();
    return Math.abs(toolbarRect.top - stickyTop) <= 2 && gridRect.top < toolbarRect.bottom - 2;
  };

  const focusTargetFor = (target) => {
    if (!(target instanceof Element)) return null;
    if (target.matches("[data-guru-collection-sort]")) return { kind: "sort" };
    if (target.matches("[data-guru-collection-search-input]")) return { kind: "search" };

    const quick = target.closest("[data-guru-collection-quick-filter]");
    if (quick) return { kind: "quick", type: quick.dataset.guruCollectionQuickFilter || "" };

    if (target.matches("[data-guru-panel-quick-control]")) {
      return { kind: "panel-quick", type: target.dataset.guruPanelQuickControl || "" };
    }

    if (target instanceof HTMLInputElement && target.closest("[data-guru-collection-filter-form]")) {
      return {
        kind: "filter",
        name: target.name,
        value: target.type === "checkbox" ? target.value : null,
        inputType: target.type,
      };
    }

    return null;
  };

  const restoreCollectionFocus = (root, descriptor, fallback = null) => {
    if (!root || (!descriptor && !fallback)) return;
    let target = null;

    if (descriptor?.kind === "sort") {
      target = root.querySelector(".guru-collection__sort-field[data-enhanced] > .guru-image-picklist__trigger, [data-guru-collection-sort]:not([aria-hidden])");
    } else if (descriptor?.kind === "search") {
      target = root.querySelector("[data-guru-collection-search-input]");
    } else if (descriptor?.kind === "quick") {
      target = Array.from(root.querySelectorAll("[data-guru-collection-quick-filter]"))
        .find((candidate) => candidate.dataset.guruCollectionQuickFilter === descriptor.type && candidate.getClientRects().length);
    } else if (descriptor?.kind === "panel-quick") {
      target = Array.from(root.querySelectorAll("[data-guru-panel-quick-control]"))
        .find((candidate) => candidate.dataset.guruPanelQuickControl === descriptor.type);
    } else if (descriptor?.kind === "filter") {
      target = Array.from(formFor(root)?.elements || []).find((candidate) => (
        candidate instanceof HTMLInputElement &&
        candidate.name === descriptor.name &&
        candidate.type === descriptor.inputType &&
        (descriptor.value === null || candidate.value === descriptor.value)
      ));
    } else if (descriptor?.kind === "grid") {
      target = root.querySelector("[data-guru-collection-grid]");
    }

    if (!(target instanceof HTMLElement) || target.disabled || !target.getClientRects().length) {
      if (fallback === "sort") target = root.querySelector(".guru-collection__sort-field[data-enhanced] > .guru-image-picklist__trigger, [data-guru-collection-sort]:not([aria-hidden])");
      else if (fallback === "search") target = root.querySelector("[data-guru-collection-search-input]");
      else target = root.querySelector("[data-guru-collection-filter-trigger]");
    }

    if (target instanceof HTMLElement && !target.disabled) target.focus({ preventScroll: true });
  };

  /*
   * Collection-search adapter contract (v1)
   * ---------------------------------------
   * GET data-guru-collection-search-endpoint with:
   *   collection_handle, scope_kind, scope_value, q, current_url, section_id, page_size,
   *   country, language, currency, price_strategy
   * The endpoint must apply country/language as Shopify Storefront API market
   * context, use price_strategy (from_price|max_price) to select the same price
   * variant as the shared Liquid price-list, and return money in currency. A
   * currency mismatch fails closed so an async result can never silently replace
   * the current-market Liquid prices.
   * Response JSON (money amounts are integer minor units):
   *   {
   *     version: 1,
   *     query: string,
   *     collection_handle: string,
   *     scope_kind: "collection" | "vendor" | "type",
   *     scope_value: string,
   *     canonical_url: same-store URL containing q,
   *     total: number,
   *     products: [{
   *       id, handle, url, title, vendor, available, one_time_available,
   *       requires_selling_plan,
   *       featured_media: { url, width, height, alt } | null,
   *       price: { amount, currency_code },
   *       compare_at_price: { amount, currency_code } | null,
   *       selected_variant: { id, available, requires_selling_plan },
   *       variants_count, badge
   *     }],
   *     pagination: { page, pages, page_size, previous_url, next_url }
   *   }
   * Applying results is intentionally delegated to
   * window.GuruCollectionSearchAdapter.applyResults({ root, payload, commit }).
   * The adapter renders a detached Array<Element> through the shared async
   * product-card renderer, then invokes commit({ cards, payload }) exactly once.
   * This controller owns that synchronous commit and replaces grid, total/empty
   * and pagination together; it never owns a parallel card template.
   * Search pagination links rendered by the adapter use
   * data-guru-collection-search-page so the same endpoint retains collection,
   * filter, sort, page and q state.
   */
  const searchEndpointUrl = (root, query, currentUrl = window.location.href) => {
    const form = root?.querySelector("[data-guru-collection-search]");
    const endpoint = form?.dataset.guruCollectionSearchEndpoint?.trim();
    if (!endpoint) return null;
    const endpointUrl = new URL(endpoint, window.location.origin);
    const scope = searchScope(root);
    endpointUrl.searchParams.set("collection_handle", root.dataset.guruCollectionHandle || "");
    endpointUrl.searchParams.set("scope_kind", scope.kind);
    endpointUrl.searchParams.set("scope_value", scope.value);
    endpointUrl.searchParams.set("q", query);
    endpointUrl.searchParams.set("current_url", authoritativeSearchUrl(root, currentUrl).href);
    endpointUrl.searchParams.set("section_id", root.dataset.guruCollectionSectionId || "");
    endpointUrl.searchParams.set("page_size", root.dataset.guruCollectionPageSize || "48");
    endpointUrl.searchParams.set("country", root.dataset.guruCollectionCountry || "");
    endpointUrl.searchParams.set("language", root.dataset.guruCollectionLanguage || "");
    endpointUrl.searchParams.set("currency", root.dataset.guruCollectionCurrency || "");
    const priceStrategy = root.dataset.guruCollectionPriceStrategy;
    endpointUrl.searchParams.set("price_strategy", priceStrategy === "max_price" ? "max_price" : "from_price");
    return endpointUrl;
  };

  const fetchSearchPayload = async (root, query, currentUrl, signal) => {
    const endpointUrl = searchEndpointUrl(root, query, currentUrl);
    if (!endpointUrl) throw new Error("Collection search endpoint is missing");
    const response = await fetch(endpointUrl, {
      headers: { Accept: "application/json" },
      signal,
    });
    if (!response.ok) {
      const error = new Error(response.status >= 500 || response.status === 429
        ? "Collection search failed"
        : "Collection search is unavailable for this surface");
      error.retryable = response.status >= 500 || response.status === 429;
      throw error;
    }
    return response.json();
  };

  const removeParamValue = (url, name, value) => {
    const values = url.searchParams.getAll(name);
    url.searchParams.delete(name);
    let removed = false;
    values.forEach((candidate) => {
      if (!removed && candidate === value) removed = true;
      else url.searchParams.append(name, candidate);
    });
    url.searchParams.delete("page");
    return url;
  };

  const syncControlsFromUrl = (root, canonicalValue) => {
    const url = cleanUrl(canonicalValue);
    const form = formFor(root);
    if (!form) return;
    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.checked = url.searchParams.getAll(input.name).includes(input.value);
      input.defaultChecked = input.checked;
    });
    form.querySelectorAll('input[type="number"]').forEach((input) => {
      input.value = url.searchParams.get(input.name) || "";
      input.defaultValue = input.value;
    });
    form.querySelectorAll("[data-guru-technical-filter]").forEach((input) => {
      input.disabled = !url.searchParams.getAll(input.name).includes(input.value);
    });
    const sort = url.searchParams.get("sort_by") || root.dataset.guruCollectionDefaultSort || "";
    form.querySelectorAll('input[type="hidden"][name="sort_by"]').forEach((input) => {
      input.value = sort;
      input.defaultValue = sort;
    });
    const select = root.querySelector("[data-guru-collection-sort]");
    if (select && Array.from(select.options).some((option) => option.value === sort)) select.value = sort;
  };

  const syncSearchFilterPresentation = (root, canonicalValue) => {
    const form = formFor(root);
    const container = root?.querySelector("[data-guru-collection-active-filters]");
    const list = root?.querySelector("[data-guru-collection-active-list]");
    const count = root?.querySelector("[data-guru-collection-filter-count]");
    const clear = root?.querySelector("[data-guru-clear-filters]");
    if (!form || !container || !list || !count || !clear) return;

    const canonicalUrl = cleanUrl(canonicalValue || window.location.href);
    const currency = root.dataset.guruCollectionCurrency || "USD";
    const locale = root.dataset.guruCollectionLanguage || document.documentElement.lang || "en";
    const currencyFormatter = new Intl.NumberFormat(locale, { style: "currency", currency });
    const entries = [];
    const checkboxGroups = new Map();
    form.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
      if (!input.name) return;
      const group = input.dataset.guruFilterGroup || `${input.name}\u0000${input.value}`;
      const baseLabel = input.dataset.guruFilterLabel || input.closest("label")?.querySelector("span")?.textContent?.trim() || input.value;
      const groupLabel = input.dataset.guruFilterGroupLabel?.trim();
      const label = root.dataset.guruCollectionShowFilterGroups === "true" && groupLabel
        ? `${groupLabel}: ${baseLabel}`
        : baseLabel;
      if (!checkboxGroups.has(group)) checkboxGroups.set(group, { label, inputs: [] });
      checkboxGroups.get(group).inputs.push(input);
    });
    checkboxGroups.forEach(({ label, inputs }) => {
      let url = new URL(canonicalUrl);
      inputs.forEach((input) => { url = removeParamValue(url, input.name, input.value); });
      entries.push({ label, url });
    });
    form.querySelectorAll(".guru-collection__filter-group").forEach((group) => {
      const fields = Array.from(group.querySelectorAll('input[type="number"]')).filter((field) => field.value !== "");
      if (fields.length === 0) return;
      const label = group.querySelector("summary")?.textContent?.trim() || "Price";
      const values = fields.map((field) => {
        const fieldLabel = field.closest("label")?.querySelector("span")?.textContent?.trim() || "";
        const amount = Number(field.value);
        const formatted = Number.isFinite(amount) ? currencyFormatter.format(amount) : field.value;
        return `${fieldLabel} ${formatted}`.trim();
      });
      const url = new URL(canonicalUrl);
      fields.forEach((field) => url.searchParams.delete(field.name));
      url.searchParams.delete("page");
      entries.push({ label: `${label}: ${values.join(" – ")}`, url });
    });

    list.replaceChildren(...entries.map(({ label, url }) => {
      const link = document.createElement("a");
      link.className = "guru-collection__active-filter guru-catalog-controls__active-filter";
      link.href = url;
      link.dataset.guruCollectionLink = "";
      link.dataset.guruActiveFilter = "";
      link.setAttribute("aria-label", localizedCopy(root, "removeFilter", { label }));
      link.append(document.createTextNode(`${label} `), createSvgIcon("close"));
      return link;
    }));
    container.hidden = entries.length === 0;
    count.hidden = entries.length === 0;
    count.textContent = `${entries.length}`;

    const clearUrl = cleanUrl(root.dataset.guruCollectionClearUrl || canonicalUrl.pathname);
    const queryParam = localSearchParam(root);
    const query = canonicalUrl.searchParams.get(queryParam)?.trim();
    if (query) clearUrl.searchParams.set(queryParam, query);
    clear.href = clearUrl;
  };

  const paginationPages = (current, pages) => {
    if (pages <= 7) return Array.from({ length: pages }, (_, index) => index + 1);
    const candidates = new Set([1, pages, current - 1, current, current + 1]);
    const values = Array.from(candidates).filter((page) => page > 0 && page <= pages).sort((a, b) => a - b);
    const output = [];
    values.forEach((page, index) => {
      if (index > 0 && page - values[index - 1] > 1) output.push("ellipsis");
      output.push(page);
    });
    return output;
  };

  const safeCollectionHref = (value, fallback) => {
    if (!value) return fallback;
    const url = cleanUrl(value);
    return url.origin === window.location.origin ? url.href : fallback;
  };

  const pageUrl = (canonicalUrl, page) => {
    const url = new URL(canonicalUrl);
    if (page <= 1) url.searchParams.delete("page");
    else url.searchParams.set("page", `${page}`);
    return url.href;
  };

  const paginationLink = (href, label, content, rel = "") => {
    const link = document.createElement("a");
    link.className = "pagination__link h6";
    link.href = href;
    link.dataset.guruCollectionSearchPage = "";
    link.setAttribute("aria-label", label);
    if (rel) link.rel = rel;
    if (content === "chevron-left" || content === "chevron-right") link.append(createSvgIcon(content));
    else link.textContent = content;
    return link;
  };

  const buildSearchPagination = (root, payload, canonicalUrl) => {
    const wrapper = document.createElement("div");
    wrapper.className = "guru-catalog-controls__pagination";
    wrapper.dataset.guruCollectionPagination = "";
    const current = Math.max(1, Number(payload.pagination.page));
    const pages = Math.max(0, Number(payload.pagination.pages));
    wrapper.hidden = pages <= 1;
    if (pages <= 1) return wrapper;

    const navigation = document.createElement("nav");
    navigation.className = "pagination";
    navigation.setAttribute("role", "navigation");
    navigation.setAttribute("aria-label", localizedCopy(root, "paginationLabel"));

    if (current > 1) {
      const fallback = pageUrl(canonicalUrl, current - 1);
      navigation.append(paginationLink(
        safeCollectionHref(payload.pagination.previous_url, fallback),
        localizedCopy(root, "goToPage", { page: current - 1 }),
        "chevron-left",
        "prev",
      ));
    }

    paginationPages(current, pages).forEach((page) => {
      if (page === "ellipsis") {
        const ellipsis = document.createElement("span");
        ellipsis.className = "pagination__link pagination__link--disabled h6";
        ellipsis.textContent = "…";
        navigation.append(ellipsis);
      } else if (page === current) {
        const selected = document.createElement("span");
        selected.className = "pagination__link pagination__link--disabled h6";
        selected.setAttribute("aria-current", "page");
        selected.textContent = `${page}`;
        navigation.append(selected);
      } else {
        navigation.append(paginationLink(pageUrl(canonicalUrl, page), localizedCopy(root, "goToPage", { page }), `${page}`));
      }
    });

    if (current < pages) {
      const fallback = pageUrl(canonicalUrl, current + 1);
      navigation.append(paginationLink(
        safeCollectionHref(payload.pagination.next_url, fallback),
        localizedCopy(root, "goToPage", { page: current + 1 }),
        "chevron-right",
        "next",
      ));
    }

    wrapper.append(navigation);
    return wrapper;
  };

  const buildSearchResults = (root, payload, cards, canonicalUrl) => {
    const cardElements = Array.from(cards || []);
    if (
      cardElements.length !== payload.products.length ||
      cardElements.some((card) => !(card instanceof Element) || !card.matches("[data-guru-product-card]"))
    ) {
      throw new Error("The shared product-card renderer returned an incomplete result");
    }

    const results = document.createElement("div");
    results.className = "guru-collection__results";
    results.dataset.guruCollectionResults = "";
    const pageMarker = document.createElement("span");
    pageMarker.hidden = true;
    pageMarker.dataset.guruCollectionCurrentPage = `${payload.pagination.page}`;
    const grid = document.createElement("div");
    grid.className = "guru-collection__grid";
    grid.dataset.guruCollectionGrid = "";
    grid.tabIndex = -1;

    if (Number(payload.total) === 0) {
      const empty = document.createElement("div");
      empty.className = "guru-collection__empty";
      const heading = document.createElement("h2");
      heading.textContent = localizedCopy(root, "zeroMatches");
      const copy = document.createElement("p");
      copy.textContent = localizedCopy(root, "emptySearchCopy");
      const reset = document.createElement("a");
      reset.className = "guru-button guru-button--primary";
      reset.href = cleanUrl(root.dataset.guruCollectionClearUrl || canonicalUrl.pathname);
      reset.dataset.guruCollectionLink = "";
      reset.textContent = localizedCopy(root, "viewAllProducts");
      empty.append(heading, copy, reset);
      grid.append(empty);
    } else {
      grid.append(...cardElements);
    }
    results.append(pageMarker, grid);

    results.append(buildSearchPagination(root, payload, canonicalUrl));
    return results;
  };

  const commitSearchResults = (root, payload, cards, canonicalUrl, options = {}) => {
    const currentResults = root?.querySelector("[data-guru-collection-results]");
    if (!currentResults) throw new Error("Collection results region is missing");
    const nextResults = buildSearchResults(root, payload, cards, canonicalUrl);
    if (options.isCurrent && !options.isCurrent()) throw new DOMException("Stale search response", "AbortError");

    disposeVideos(currentResults);
    currentResults.replaceWith(nextResults);
    const count = Math.max(0, Number(payload.total));
    const resultCount = root.querySelector("[data-guru-result-count-value]");
    const resultNoun = root.querySelector("[data-guru-result-count-noun]");
    if (resultCount) resultCount.textContent = `${count}`;
    if (resultNoun) resultNoun.textContent = productNoun(root, count);
    const input = root.querySelector("[data-guru-collection-search-input]");
    if (input) input.value = payload.query || "";
    syncControlsFromUrl(root, canonicalUrl);
    syncSearchFilterPresentation(root, canonicalUrl);
    syncQuickFilters(root);
    setDraftFacetCountsAvailable(formFor(root), false);
    updateSortValue(root);
    if (options.updateHistory !== false) {
      history.pushState({ guruCollection: true, query: payload.query }, "", canonicalUrl);
    }
    root.dispatchEvent(new CustomEvent("guru:collection-search:committed", {
      bubbles: true,
      detail: { total: count, page: Number(payload.pagination.page), canonicalUrl: canonicalUrl.href },
    }));
    return root;
  };

  const validateSearchPayload = (root, payload, expectedUrlValue = null) => {
    const validProducts = Array.isArray(payload?.products) && payload.products.every((product) => (
      Number.isSafeInteger(Number(product?.id)) &&
      typeof product.handle === "string" &&
      typeof product.url === "string" &&
      typeof product.title === "string" &&
      Number.isFinite(Number(product.price?.amount)) &&
      typeof product.price?.currency_code === "string" &&
      Number.isSafeInteger(Number(product.selected_variant?.id)) &&
      typeof product.selected_variant?.available === "boolean" &&
      Number.isFinite(Number(product.variants_count)) &&
      (product.featured_media === null || typeof product.featured_media?.url === "string")
    ));
    const total = payload?.total;
    const page = payload?.pagination?.page;
    const pages = payload?.pagination?.pages;
    const pageSize = payload?.pagination?.page_size;
    const configuredPageSize = Number(root?.dataset.guruCollectionPageSize);
    const validPagination = payload?.pagination &&
      isPositiveSafeInteger(page) &&
      isPositiveSafeInteger(pages) &&
      isPositiveSafeInteger(pageSize) &&
      pageSize === configuredPageSize &&
      page <= pages;
    if (
      payload?.version !== 1 ||
      !validProducts ||
      !validPagination ||
      typeof payload.query !== "string" ||
      !isNonNegativeSafeInteger(total)
    ) {
      throw new Error("Invalid collection-search response");
    }

    const expectedPages = Math.max(1, Math.ceil(total / pageSize));
    const expectedCardCount = Math.max(0, Math.min(pageSize, total - ((page - 1) * pageSize)));
    if (pages !== expectedPages || payload.products.length !== expectedCardCount) {
      throw new Error("Collection-search pagination does not match the result");
    }
    if (payload.collection_handle !== root?.dataset.guruCollectionHandle) {
      throw new Error("Collection-search response belongs to another collection");
    }
    const scope = searchScope(root);
    if (payload.scope_kind !== scope.kind || payload.scope_value !== scope.value) {
      throw new Error("Collection-search response belongs to another search scope");
    }
    const marketCurrency = root?.dataset.guruCollectionCurrency;
    if (marketCurrency && payload.products.some((product) => (
      product.price.currency_code !== marketCurrency ||
      (product.compare_at_price && product.compare_at_price.currency_code !== marketCurrency)
    ))) {
      throw new Error("Collection-search prices do not match the active Shopify Market");
    }
    if (typeof payload.canonical_url !== "string" || payload.canonical_url === "") {
      throw new Error("Collection-search response has an invalid canonical URL");
    }
    const canonicalUrl = cleanUrl(payload.canonical_url);
    const queryParam = localSearchParam(root);
    const canonicalPageValues = canonicalUrl.searchParams.getAll("page");
    const canonicalPageIsValid = page === 1
      ? canonicalPageValues.length === 0
      : canonicalPageValues.length === 1 && canonicalPageValues[0] === `${page}`;
    if (
      !isCanonicalCollectionUrl(root, canonicalUrl) ||
      canonicalUrl.searchParams.getAll(queryParam).length !== 1 ||
      canonicalUrl.searchParams.get(queryParam) !== payload.query ||
      !canonicalPageIsValid ||
      (page === 1 ? payload.pagination.previous_url !== null : !isExpectedPageUrl(root, payload.pagination.previous_url, canonicalUrl, page - 1)) ||
      (page === pages ? payload.pagination.next_url !== null : !isExpectedPageUrl(root, payload.pagination.next_url, canonicalUrl, page + 1))
    ) {
      throw new Error("Collection-search response has an invalid canonical URL");
    }
    if (expectedUrlValue) {
      const expectedUrl = cleanUrl(expectedUrlValue);
      if (
        !isCanonicalCollectionUrl(root, expectedUrl) ||
        sortedCollectionStateEntries(root, canonicalUrl).join("\u0001") !== sortedCollectionStateEntries(root, expectedUrl).join("\u0001")
      ) {
        throw new Error("Collection-search response does not match the requested filter state");
      }
    }
    return canonicalUrl;
  };

  const applySearchResponse = async (root, payload, options = {}) => {
    const canonicalUrl = validateSearchPayload(root, payload, options.currentUrl);
    const adapter = window.GuruCollectionSearchAdapter;
    if (!adapter || typeof adapter.applyResults !== "function") {
      throw new Error("The shared async product-card renderer is unavailable");
    }
    if (options.isCurrent && !options.isCurrent()) {
      throw new DOMException("Stale search response", "AbortError");
    }
    let committed = false;
    const commit = ({ cards, payload: committedPayload } = {}) => {
      if (committed) throw new Error("Collection-search result was committed more than once");
      if (committedPayload && committedPayload !== payload) {
        throw new Error("Collection-search adapter attempted to commit another response");
      }
      if (options.isCurrent && !options.isCurrent()) {
        throw new DOMException("Stale search response", "AbortError");
      }
      const committedRoot = commitSearchResults(root, payload, cards, canonicalUrl, options);
      committed = true;
      return committedRoot;
    };
    const applied = await adapter.applyResults({ root, payload, commit });
    if (applied === false || !committed) throw new Error("Collection-search result could not be rendered");
    if (options.isCurrent && !options.isCurrent()) throw new DOMException("Stale search response", "AbortError");
    const adapterRoot = applied instanceof Element ? applied : applied?.root;
    const nextRoot = rootFor(adapterRoot || root);
    const total = Number(payload.total) || 0;
    setLiveMessage(nextRoot, localizedCopy(nextRoot, "showCount", {
      count: total,
      noun: productNoun(nextRoot, total),
    }));
    if (options.scrollToGrid) {
      scrollCollectionGridToStart(nextRoot);
    }
    restoreCollectionFocus(nextRoot, options.focusTarget, options.focusFallback || options.restoreFocus || null);
    initVideos(sectionFor(nextRoot) || nextRoot);
    enhanceInfiniteLoading(nextRoot);
    failedSearch = null;
    return nextRoot;
  };

  const performCollectionSearch = async (root, query, options = {}) => {
    const form = root?.querySelector("[data-guru-collection-search]");
    const input = form?.querySelector("[data-guru-collection-search-input]");
    if (!form || !input) return;
    const normalizedQuery = `${query || ""}`.trim().replace(/\s+/g, " ");
    failedNavigation = null;
    failedSearch = null;

    if (normalizedQuery === "") {
      cancelSearch(root, form);
      setError(root);
      form.removeAttribute("aria-busy");
      const currentUrl = new URL(window.location.href);
      const queryParam = localSearchParam(root);
      if (currentUrl.searchParams.has(queryParam)) {
        currentUrl.searchParams.delete(queryParam);
        navigateCollection(root, currentUrl, {
          focusTarget: { kind: "search" },
          focusFallback: "search",
          scrollToGrid: options.scrollToGrid,
          updateHistory: options.updateHistory,
        });
      }
      return;
    }

    if (normalizedQuery.length < 2 || normalizedQuery.length > 80) {
      cancelSearch(root, form);
      setError(root, localizedCopy(root, "searchLengthError"), false);
      setLiveMessage(root, localizedCopy(root, "searchLengthStatus"));
      form.removeAttribute("aria-busy");
      return;
    }

    const endpoint = form.dataset.guruCollectionSearchEndpoint?.trim();
    if (!endpoint) {
      cancelSearch(root, form);
      setError(root, localizedCopy(root, "searchUnavailable"), false);
      setLiveMessage(root, localizedCopy(root, "searchUnavailable"));
      form.removeAttribute("aria-busy");
      return;
    }

    if (typeof window.GuruCollectionSearchAdapter?.applyResults !== "function") {
      cancelSearch(root, form);
      setError(root, localizedCopy(root, "searchUnavailable"), false);
      setLiveMessage(root, localizedCopy(root, "searchUnavailable"));
      return;
    }

    cleanupInfiniteLoading(root);
    searchRequests.get(form)?.abort();
    const controller = new AbortController();
    searchRequests.set(form, controller);
    collectionRequest?.abort();
    const requestedUrl = authoritativeSearchUrl(root, options.currentUrl || window.location.href);
    form.setAttribute("aria-busy", "true");
    claimBusy(root, controller);
    setError(root);
    setLiveMessage(root, localizedCopy(root, "searching"));

    try {
      const payload = await fetchSearchPayload(root, normalizedQuery, requestedUrl, controller.signal);
      if (!root.isConnected || searchRequests.get(form) !== controller) {
        throw new DOMException("Stale search response", "AbortError");
      }
      if (payload.query !== normalizedQuery) throw new Error("Search response does not match the query");
      await applySearchResponse(root, payload, {
        ...options,
        currentUrl: requestedUrl,
        isCurrent: () => searchRequests.get(form) === controller && root.isConnected,
      });
    } catch (error) {
      if (error?.name === "AbortError") return;
      const retryable = error?.retryable !== false;
      failedSearch = retryable ? { query: normalizedQuery, options: { ...options, currentUrl: requestedUrl } } : null;
      setError(root, localizedCopy(root, "searchFailed"), retryable);
      setLiveMessage(root, localizedCopy(root, "searchFailedStatus"));
    } finally {
      if (searchRequests.get(form) === controller) {
        searchRequests.delete(form);
        form.removeAttribute("aria-busy");
      }
      releaseBusy(root, controller);
    }
  };

  const queueCollectionSearch = (root, options = {}) => {
    const form = root?.querySelector("[data-guru-collection-search]");
    const input = form?.querySelector("[data-guru-collection-search-input]");
    if (!form || !input) return;
    window.clearTimeout(searchTimers.get(form));
    searchTimers.delete(form);
    cancelSearch(root, form);
    const timer = window.setTimeout(() => {
      if (searchTimers.get(form) !== timer) return;
      searchTimers.delete(form);
      performCollectionSearch(root, input.value, options);
    }, 320);
    searchTimers.set(form, timer);
  };

  const updateSortValue = (root) => {
    const select = root?.querySelector("[data-guru-collection-sort]");
    const value = root?.querySelector("[data-guru-collection-sort-value]");
    if (select && value) value.textContent = select.selectedOptions[0]?.textContent?.trim() || "";
  };

  const resetDraftEvidence = (root) => {
    const form = formFor(root);
    if (!form) return;
    resetFacetEvidence(form);
    const hasSearchQuery = Boolean(localSearchQuery(root));
    setDraftFacetCountsAvailable(form, !hasSearchQuery);
  };

  const invalidateDraftPreview = (root, { resetApply = false } = {}) => {
    const form = formFor(root);
    if (!form) return;
    previewGenerations.set(form, (previewGenerations.get(form) || 0) + 1);
    window.clearTimeout(previewTimers.get(form));
    previewTimers.delete(form);
    previewRequests.get(form)?.abort();
    previewRequests.delete(form);
    if (resetApply) {
      const apply = root?.querySelector("[data-guru-collection-filter-apply]");
      const count = Number(root?.querySelector("[data-guru-result-count-value]")?.textContent?.trim() || 0);
      if (apply) {
        apply.disabled = count === 0;
        apply.textContent = count === 0
          ? localizedCopy(root, "zeroMatches")
          : localizedCopy(root, "showCount", { count, noun: productNoun(root, count) });
      }
    }
  };

  const closeFilters = (root, { restoreFocus = true, resetDraft = true } = {}) => {
    const dialog = dialogFor(root);
    if (!dialog) return;
    invalidateDraftPreview(root, { resetApply: resetDraft });
    if (dialog.open) dialog.close();
    root.querySelector("[data-guru-collection-layout]")?.classList.remove("is-filter-visible");
    document.body.classList.remove("guru-collection-filter-open");
    const trigger = root.querySelector("[data-guru-collection-filter-trigger]");
    trigger?.setAttribute("aria-expanded", "false");
    const label = trigger?.querySelector("[data-guru-collection-filter-label]");
    if (label) label.textContent = localizedCopy(root, "filters");
    if (resetDraft) {
      formFor(root)?.reset();
      resetDraftEvidence(root);
    }
    syncQuickFilters(root);
    const resumePausedSearch = resetDraft && searchPausedForDraft.has(root);
    searchPausedForDraft.delete(root);
    if (resumePausedSearch) {
      queueCollectionSearch(root, { updateHistory: false, currentUrl: window.location.href });
    }
    if (restoreFocus) trigger?.focus({ preventScroll: true });
  };

  const openFilters = (root, { restoreFocus = false } = {}) => {
    const dialog = dialogFor(root);
    const trigger = root?.querySelector("[data-guru-collection-filter-trigger]");
    if (!root || !dialog || dialog.open) return;

    if (!desktopQuery.matches) {
      dialog.querySelectorAll("details.guru-catalog-controls__filter-group").forEach((group) => { group.open = false; });
    }

    if (desktopQuery.matches && root.dataset.guruCollectionFilterLayout === "sidebar") {
      dialog.show();
      root.querySelector("[data-guru-collection-layout]")?.classList.add("is-filter-visible");
      const label = trigger?.querySelector("[data-guru-collection-filter-label]");
      if (label) label.textContent = localizedCopy(root, "hideFilters");
      if (restoreFocus) trigger?.focus({ preventScroll: true });
    } else {
      dialog.showModal();
      document.body.classList.add("guru-collection-filter-open");
    }

    trigger?.setAttribute("aria-expanded", "true");
  };

  const initVideos = (scope = document) => {
    scope.querySelectorAll?.("[data-guru-collection-story]").forEach((story) => {
      const videos = Array.from(story.querySelectorAll("video"));
      if (videos.length === 0 || initializedVideos.has(story)) return;
      initializedVideos.add(story);
      videos.forEach((video) => { video.controls = false; });
      let observer = null;
      let isIntersecting = !("IntersectionObserver" in window);

      const visibleVideo = () => videos.find((video) => getComputedStyle(video).display !== "none") || videos[0];
      const pauseAll = () => videos.forEach((video) => video.pause());
      const reconcilePlayback = () => {
        const current = visibleVideo();
        videos.forEach((video) => { if (video !== current) video.pause(); });
        if (reducedMotionQuery.matches || !isIntersecting) {
          current.pause();
          return;
        }
        current.play().catch(() => {});
      };

      const onReducedMotionChange = () => {
        if (reducedMotionQuery.matches) pauseAll();
        else reconcilePlayback();
      };
      const onStoryViewportChange = () => {
        pauseAll();
        reconcilePlayback();
      };
      reducedMotionQuery.addEventListener("change", onReducedMotionChange);
      storyMobileQuery.addEventListener("change", onStoryViewportChange);

      const cleanup = () => {
        observer?.disconnect();
        reducedMotionQuery.removeEventListener("change", onReducedMotionChange);
        storyMobileQuery.removeEventListener("change", onStoryViewportChange);
        pauseAll();
        initializedVideos.delete(story);
        videoCleanups.delete(story);
      };
      videoCleanups.set(story, cleanup);

      if ("IntersectionObserver" in window) {
        observer = new IntersectionObserver((entries) => {
          isIntersecting = Boolean(entries[0]?.isIntersecting);
          reconcilePlayback();
        }, { threshold: 0.42 });
        observer.observe(story);
      }
      reconcilePlayback();
    });
  };

  const disposeVideos = (scope) => {
    scope?.querySelectorAll?.("[data-guru-collection-story]").forEach((story) => videoCleanups.get(story)?.());
  };

  const nativeFilterStateEntries = (params, defaultSort = "") => {
    const normalized = new URLSearchParams();
    params.forEach((value, name) => {
      if (name.startsWith("filter.") || name === "sort_by") {
        const trimmedValue = `${value}`.trim();
        if (trimmedValue === "") return;
        if (name === "filter.v.price.gte" || name === "filter.v.price.lte") {
          const numericValue = Number(trimmedValue);
          if (!Number.isFinite(numericValue) || numericValue < 0) {
            throw new Error("Collection section response has an invalid price filter");
          }
          normalized.append(name, `${numericValue}`);
        } else {
          normalized.append(name, value);
        }
      }
    });
    if (!normalized.has("sort_by") && defaultSort) normalized.set("sort_by", defaultSort);
    return Array.from(normalized.entries())
      .sort(([leftName, leftValue], [rightName, rightValue]) => (
        leftName.localeCompare(rightName) || leftValue.localeCompare(rightValue)
      ))
      .map(([name, value]) => `${name}\u0000${value}`);
  };

  const validateNativeCollectionSection = (root, incomingSection, requestedUrlValue) => {
    const requestedUrl = cleanUrl(requestedUrlValue);
    const incomingRoot = incomingSection?.querySelector(rootSelector);
    const incomingForm = incomingRoot?.querySelector("[data-guru-collection-filter-form]");
    const incomingResults = incomingRoot?.querySelector("[data-guru-collection-results]");
    const incomingPageMarker = incomingResults?.querySelector("[data-guru-collection-current-page]");
    const incomingCountCopy = incomingRoot?.querySelector("[data-guru-result-count-value]")?.textContent?.trim() || "";
    const total = /^\d+$/.test(incomingCountCopy) ? Number(incomingCountCopy) : NaN;
    const pageSize = Number(root?.dataset.guruCollectionPageSize);
    const pageValues = requestedUrl.searchParams.getAll("page");
    const requestedPage = pageValues.length === 0 ? 1 : Number(pageValues[0]);
    const incomingPage = Number(incomingPageMarker?.dataset.guruCollectionCurrentPage);
    const expectedSectionId = root?.dataset.guruCollectionSectionId || "";
    const expectedPath = expectedCollectionPath(root);
    const currentForm = formFor(root);
    const currentHasFilters = Boolean(currentForm);
    const incomingHasFilters = Boolean(incomingForm);
    const incomingClearValue = incomingRoot?.dataset.guruCollectionClearUrl || "";
    const incomingActionValue = incomingForm?.getAttribute("action") || "";
    const incomingClearUrl = cleanUrl(incomingClearValue);
    const incomingActionUrl = incomingActionValue ? cleanUrl(incomingActionValue) : null;

    if (
      !isCanonicalCollectionUrl(root, requestedUrl) ||
      !expectedSectionId ||
      incomingSection?.id !== `shopify-section-${expectedSectionId}` ||
      !incomingRoot ||
      incomingRoot.dataset.guruCollectionSectionId !== expectedSectionId ||
      !incomingClearValue ||
      currentHasFilters !== incomingHasFilters ||
      incomingRoot.dataset.guruCollectionHandle !== root.dataset.guruCollectionHandle ||
      incomingRoot.dataset.guruCollectionSearchScopeKind !== root.dataset.guruCollectionSearchScopeKind ||
      incomingRoot.dataset.guruCollectionSearchScopeValue !== root.dataset.guruCollectionSearchScopeValue ||
      incomingRoot.dataset.guruCollectionSearchQueryParam !== root.dataset.guruCollectionSearchQueryParam ||
      normalizedPathname(incomingClearUrl.pathname) !== expectedPath ||
      incomingClearUrl.origin !== window.location.origin ||
      (currentHasFilters && (
        !incomingActionUrl ||
        normalizedPathname(incomingActionUrl.pathname) !== expectedPath ||
        incomingActionUrl.origin !== window.location.origin
      )) ||
      !incomingResults ||
      pageValues.length > 1 ||
      !isPositiveSafeInteger(requestedPage) ||
      incomingPage !== requestedPage ||
      !isPositiveSafeInteger(pageSize) ||
      !isNonNegativeSafeInteger(total)
    ) {
      throw new Error("Collection section response has an invalid identity or pagination");
    }

    const defaultSort = root.dataset.guruCollectionDefaultSort || "";
    const expectedState = nativeFilterStateEntries(requestedUrl.searchParams, defaultSort);
    const incomingParams = incomingForm
      ? new URLSearchParams(new FormData(incomingForm))
      : new URLSearchParams();
    if (!incomingForm) {
      if (Array.from(requestedUrl.searchParams.keys()).some((name) => name.startsWith("filter."))) {
        throw new Error("Collection section response is missing the requested filter form");
      }
      const incomingSort = incomingRoot.querySelector("[data-guru-collection-sort]")?.value || defaultSort;
      if (incomingSort) incomingParams.set("sort_by", incomingSort);
    }
    const incomingState = nativeFilterStateEntries(incomingParams, defaultSort);
    if (expectedState.join("\u0001") !== incomingState.join("\u0001")) {
      throw new Error("Collection section response does not match the requested filter state");
    }

    const pages = Math.max(1, Math.ceil(total / pageSize));
    const expectedCardCount = Math.max(0, Math.min(pageSize, total - ((requestedPage - 1) * pageSize)));
    const renderedCardCount = incomingResults.querySelectorAll("[data-guru-collection-grid] > [data-guru-product-card]").length;
    if (requestedPage > pages || renderedCardCount !== expectedCardCount) {
      throw new Error("Collection section response has an inconsistent product count");
    }

    return { incomingRoot, incomingForm, incomingResults, total };
  };

  const productCardIdentity = (card) => card?.dataset?.guruProductId?.trim() || "";

  const validatedInfiniteCards = (grid, cards) => {
    const currentIds = new Set(Array.from(grid.querySelectorAll(":scope > [data-guru-product-card]"))
      .map(productCardIdentity)
      .filter(Boolean));
    const nextCards = Array.from(cards || []);
    const nextIds = new Set();
    const hasInvalidCard = nextCards.some((card) => {
      const identity = productCardIdentity(card);
      if (!card.matches?.("[data-guru-product-card]") || !identity || currentIds.has(identity) || nextIds.has(identity)) {
        return true;
      }
      nextIds.add(identity);
      return false;
    });
    if (nextCards.length === 0 || hasInvalidCard) {
      throw new Error("Next collection page contains invalid or duplicate product cards");
    }
    return nextCards;
  };

  const cleanupInfiniteLoading = (root) => {
    const state = infiniteLoadingStates.get(root);
    state?.observer?.disconnect();
    state?.controller?.abort();
    state?.sentinel?.remove();
    infiniteLoadingStates.delete(root);
    root?.removeAttribute("data-guru-catalog-infinite");
  };

  const renderSearchCards = async (root, payload, isCurrent) => {
    const adapter = window.GuruCollectionSearchAdapter;
    if (!adapter || typeof adapter.applyResults !== "function") {
      throw new Error("The shared async product-card renderer is unavailable");
    }
    let renderedCards = null;
    let committed = false;
    const commit = ({ cards, payload: committedPayload } = {}) => {
      if (committed || (committedPayload && committedPayload !== payload) || !isCurrent()) {
        throw new DOMException("Stale or invalid collection-search response", "AbortError");
      }
      renderedCards = Array.from(cards || []);
      committed = true;
      return root;
    };
    const applied = await adapter.applyResults({ root, payload, commit });
    if (applied === false || !committed || !isCurrent()) {
      throw new DOMException("Collection-search result could not be appended", "AbortError");
    }
    if (renderedCards.length !== payload.products.length) {
      throw new Error("The shared product-card renderer returned an incomplete result");
    }
    return renderedCards;
  };

  const commitInfinitePage = (root, cards, pagination, page) => {
    const grid = root?.querySelector("[data-guru-collection-grid]");
    const currentPagination = root?.querySelector("[data-guru-collection-pagination]");
    const marker = root?.querySelector("[data-guru-collection-current-page]");
    if (!grid || !currentPagination || !pagination || !marker) {
      throw new Error("Collection-resultområdet kan ikke udvides");
    }
    const nextCards = validatedInfiniteCards(grid, cards);
    grid.append(...nextCards);
    marker.dataset.guruCollectionCurrentPage = `${page}`;
    currentPagination.replaceWith(pagination);
    return nextCards.length;
  };

  const loadNextCollectionPage = async (root) => {
    const state = infiniteLoadingStates.get(root);
    if (!state || state.loading || !root.isConnected) return false;
    state.loading = true;
    state.observer.disconnect();
    state.fallback.hidden = true;
    state.loadingCopy.hidden = false;
    const controller = new AbortController();
    state.controller = controller;
    claimBusy(root, controller);
    setLiveMessage(root, localizedCopy(root, "loadingMore"));

    try {
      const nextUrl = cleanUrl(state.nextUrl);
      const page = Number(nextUrl.searchParams.get("page") || 1);
      let cards;
      let pagination;
      const query = localSearchQuery(root, nextUrl);
      if (query) {
        const payload = await fetchSearchPayload(root, query, nextUrl, controller.signal);
        const canonicalUrl = validateSearchPayload(root, payload, nextUrl);
        const isCurrent = () => infiniteLoadingStates.get(root) === state && state.controller === controller && root.isConnected;
        cards = await renderSearchCards(root, payload, isCurrent);
        if (!isCurrent()) throw new DOMException("Stale collection response", "AbortError");
        pagination = buildSearchPagination(root, payload, canonicalUrl);
      } else {
        const section = sectionFor(root);
        const sectionId = root.dataset.guruCollectionSectionId || section?.id?.replace("shopify-section-", "");
        if (!sectionId) throw new Error("Collection section id is missing");
        const requestUrl = new URL(nextUrl);
        requestUrl.searchParams.set("section_id", sectionId);
        const response = await fetch(requestUrl, {
          headers: { "X-Requested-With": "XMLHttpRequest" },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const incomingSection = parseSection(await response.text(), sectionId);
        if (infiniteLoadingStates.get(root) !== state || state.controller !== controller || !root.isConnected) {
          throw new DOMException("Stale collection response", "AbortError");
        }
        const validated = validateNativeCollectionSection(root, incomingSection, nextUrl);
        cards = Array.from(validated.incomingResults.querySelectorAll("[data-guru-collection-grid] > [data-guru-product-card]"))
          .map((card) => document.importNode(card, true));
        const incomingPagination = validated.incomingResults.querySelector("[data-guru-collection-pagination]");
        if (!incomingPagination) throw new Error("Next collection page is missing pagination");
        pagination = document.importNode(incomingPagination, true);
      }

      const loaded = commitInfinitePage(root, cards, pagination, page);
      state.controller = null;
      releaseBusy(root, controller);
      cleanupInfiniteLoading(root);
      setLiveMessage(root, localizedCopy(root, "loadedMore", { count: loaded }));
      root.dispatchEvent(new CustomEvent("guru:collection:page-appended", {
        bubbles: true,
        detail: { loaded, page, url: nextUrl.href },
      }));
      enhanceInfiniteLoading(root);
      return true;
    } catch (error) {
      if (error?.name === "AbortError") return false;
      state.controller = null;
      state.loading = false;
      state.loadingCopy.hidden = true;
      state.fallback.hidden = false;
      state.fallback.textContent = localizedCopy(root, "loadMoreRetry");
      setLiveMessage(root, localizedCopy(root, "loadMoreFailed"));
      return false;
    } finally {
      releaseBusy(root, controller);
    }
  };

  const enhanceInfiniteLoading = (root) => {
    cleanupInfiniteLoading(root);
    const pagination = root?.querySelector("[data-guru-collection-pagination]");
    if (!pagination || !("IntersectionObserver" in window)) return;
    const next = pagination?.querySelector('a[rel="next"]');
    if (!(next instanceof HTMLAnchorElement)) {
      pagination.hidden = true;
      root.dataset.guruCatalogInfinite = "complete";
      return;
    }

    const sentinel = document.createElement("div");
    sentinel.className = "guru-catalog-controls__infinite";
    sentinel.dataset.guruCollectionInfiniteSentinel = "";
    const loadingCopy = document.createElement("span");
    loadingCopy.className = "guru-catalog-controls__infinite-loading";
    loadingCopy.dataset.guruCollectionInfiniteLoading = "";
    loadingCopy.hidden = true;
    loadingCopy.setAttribute("aria-hidden", "true");
    loadingCopy.textContent = localizedCopy(root, "loadingMore");
    const fallback = document.createElement("a");
    fallback.className = "guru-button guru-button--secondary guru-catalog-controls__infinite-fallback";
    fallback.dataset.guruCollectionLoadMore = "";
    fallback.href = next.href;
    fallback.hidden = true;
    fallback.textContent = localizedCopy(root, "loadMore");
    sentinel.append(loadingCopy, fallback);
    pagination.append(sentinel);
    pagination.hidden = false;
    root.dataset.guruCatalogInfinite = "ready";

    const state = {
      controller: null,
      fallback,
      loading: false,
      loadingCopy,
      nextUrl: next.href,
      observer: null,
      sentinel,
    };
    state.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadNextCollectionPage(root);
    }, { rootMargin: "600px 0px" });
    infiniteLoadingStates.set(root, state);
    state.observer.observe(sentinel);
  };

  const replaceCollection = async (root, requestedUrl, options = {}) => {
    const section = sectionFor(root);
    const sectionId = root?.dataset.guruCollectionSectionId || section?.id?.replace("shopify-section-", "");
    if (!section || !sectionId) return false;

    cleanupInfiniteLoading(root);
    collectionRequest?.abort();
    const searchForm = root.querySelector("[data-guru-collection-search]");
    searchRequests.get(searchForm)?.abort();
    searchForm?.removeAttribute("aria-busy");
    const controller = new AbortController();
    collectionRequest = controller;
    const cleanRequestedUrl = cleanUrl(requestedUrl);
    const requestUrl = new URL(cleanRequestedUrl);
    requestUrl.searchParams.set("section_id", sectionId);
    claimBusy(root, controller);
    setError(root);
    setLiveMessage(root, localizedCopy(root, "updating"));
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    try {
      const response = await fetch(requestUrl, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const incomingSection = parseSection(await response.text(), sectionId);
      if (collectionRequest !== controller || !root.isConnected) {
        throw new DOMException("Stale collection response", "AbortError");
      }
      if (!incomingSection) throw new Error("Section markup is missing");
      validateNativeCollectionSection(root, incomingSection, cleanRequestedUrl);
      const incomingCatalog = incomingSection.querySelector(".guru-collection__catalog");
      const currentCatalog = root.querySelector(".guru-collection__catalog");
      if (!incomingCatalog || !currentCatalog) throw new Error("Collection catalog markup is missing");

      const nextCatalog = document.importNode(incomingCatalog, true);
      // DOMParser parses <noscript> descendants as live elements because its
      // synthetic document has scripting disabled. Never activate the no-JS
      // fallback (and duplicate its form controls) during an AJAX replacement.
      nextCatalog.querySelectorAll("noscript").forEach((node) => node.remove());
      disposeVideos(currentCatalog);
      currentCatalog.replaceWith(nextCatalog);
      const nextRoot = root;

      if (options.updateHistory !== false) {
        history.pushState({ guruCollection: true }, "", cleanRequestedUrl);
      }

      if (options.scrollToGrid) {
        scrollCollectionGridToStart(nextRoot);
      }

      if (options.keepDesktopFilters && desktopQuery.matches) {
        openFilters(nextRoot);
      }
      restoreCollectionFocus(nextRoot, options.focusTarget, options.focusFallback || options.restoreFocus || null);

      const shownCount = Number(nextRoot?.querySelector("[data-guru-result-count-value]")?.textContent?.trim() || 0);
      setLiveMessage(nextRoot, localizedCopy(nextRoot, "showCount", {
        count: shownCount,
        noun: productNoun(nextRoot, shownCount),
      }));
      syncQuickFilters(nextRoot);
      updateSortValue(nextRoot);
      initVideos(section);
      enhanceInfiniteLoading(nextRoot);
      failedNavigation = null;
      failedSearch = null;
      return true;
    } catch (error) {
      if (error?.name === "AbortError") return false;
      failedNavigation = { url: cleanRequestedUrl, options: { ...options } };
      setError(root, localizedCopy(root, "updateFailed"));
      setLiveMessage(root, localizedCopy(root, "productsUpdateFailedStatus"));
      return false;
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      releaseBusy(root, controller);
      if (collectionRequest === controller) collectionRequest = null;
    }
  };

  const navigateCollection = (root, requestedUrl, options = {}) => {
    const url = cleanUrl(requestedUrl);
    const query = localSearchQuery(root, url);
    if (query) return performCollectionSearch(root, query, { ...options, currentUrl: url });
    return replaceCollection(root, url, options);
  };

  const setDraftFacetCountsAvailable = (form, available) => {
    form?.querySelectorAll("[data-guru-facet-count]").forEach((count) => {
      count.hidden = !available;
      count.setAttribute("aria-hidden", String(!available));
    });
  };

  const syncDraftFacetEvidence = (root, form, incomingForm) => {
    Array.from(form.elements).forEach((field) => {
      if (!(field instanceof HTMLInputElement) || field.type !== "checkbox" || !field.name) return;
      const candidate = matchingFacetInput(incomingForm, field);
      if (!candidate) return;
      field.disabled = candidate.disabled;
      const currentCount = field.closest("label")?.querySelector("[data-guru-facet-count]");
      const incomingCount = candidate.closest("label")?.querySelector("[data-guru-facet-count]");
      if (currentCount && incomingCount) currentCount.textContent = incomingCount.textContent;
    });

    form.querySelectorAll("[data-guru-panel-quick-control]").forEach((control) => {
      const type = control.dataset.guruPanelQuickControl;
      const incomingControl = incomingForm.querySelector(`[data-guru-panel-quick-control="${type}"]`);
      if (!incomingControl) return;
      control.disabled = incomingControl.disabled;
      const currentCount = control.closest("label")?.querySelector("[data-guru-facet-count]");
      const incomingCount = incomingControl.closest("label")?.querySelector("[data-guru-facet-count]");
      if (currentCount && incomingCount) currentCount.textContent = incomingCount.textContent;
    });

    setDraftFacetCountsAvailable(form, true);
    syncQuickFilters(root);
  };

  const updateDraftPreview = async (root, generation) => {
    const form = formFor(root);
    const apply = root?.querySelector("[data-guru-collection-filter-apply]");
    const section = sectionFor(root);
    const sectionId = root?.dataset.guruCollectionSectionId || section?.id?.replace("shopify-section-", "");
    const dialog = dialogFor(root);
    if (!form || !apply || !sectionId || !dialog?.open || previewGenerations.get(form) !== generation) return;

    previewRequests.get(form)?.abort();
    const controller = new AbortController();
    previewRequests.set(form, controller);
    const isCurrent = () => (
      previewGenerations.get(form) === generation
      && previewRequests.get(form) === controller
      && root.isConnected
      && dialog.open
    );
    apply.disabled = true;
    apply.textContent = localizedCopy(root, "updating");

    const url = urlFromForm(form);

    try {
      const query = localSearchQuery(root, url);
      let count = 0;
      let incoming = null;
      if (query) {
        const payload = await fetchSearchPayload(root, query, url, controller.signal);
        if (!isCurrent()) {
          throw new DOMException("Stale filter preview", "AbortError");
        }
        if (payload.query !== query) {
          throw new Error("Invalid collection-search preview response");
        }
        validateSearchPayload(root, payload, url);
        count = payload.total;
        setDraftFacetCountsAvailable(form, false);
      } else {
        url.searchParams.set("section_id", sectionId);
        const response = await fetch(url, {
          headers: { "X-Requested-With": "XMLHttpRequest" },
          signal: controller.signal,
        });
        if (!isCurrent()) throw new DOMException("Stale filter preview", "AbortError");
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        incoming = parseSection(await response.text(), sectionId);
        if (!isCurrent()) {
          throw new DOMException("Stale filter preview", "AbortError");
        }
        if (!incoming) throw new Error("Section markup is missing from filter preview");
        count = validateNativeCollectionSection(root, incoming, url).total;
      }
      if (!isCurrent()) return;
      apply.disabled = count === 0;
      apply.textContent = count === 0
        ? localizedCopy(root, "zeroMatches")
        : localizedCopy(root, "showCount", { count, noun: productNoun(root, count) });

      const previewForm = incoming?.querySelector("[data-guru-collection-filter-form]");
      if (previewForm) syncDraftFacetEvidence(root, form, previewForm);
    } catch (error) {
      if (error?.name === "AbortError") return;
      if (!isCurrent()) return;
      setDraftFacetCountsAvailable(form, false);
      apply.disabled = false;
      apply.textContent = localizedCopy(root, "showProducts");
    } finally {
      if (previewRequests.get(form) === controller) previewRequests.delete(form);
    }
  };

  const queueDraftPreview = (root) => {
    const form = formFor(root);
    if (!form) return;
    const searchForm = root.querySelector("[data-guru-collection-search]");
    const queuedSearch = searchTimers.get(searchForm);
    const activeSearch = searchRequests.get(searchForm);
    if (queuedSearch || activeSearch) {
      window.clearTimeout(queuedSearch);
      searchTimers.delete(searchForm);
      cancelSearch(root, searchForm);
      searchPausedForDraft.add(root);
    }
    const generation = (previewGenerations.get(form) || 0) + 1;
    previewGenerations.set(form, generation);
    window.clearTimeout(previewTimers.get(form));
    previewRequests.get(form)?.abort();
    previewRequests.delete(form);
    const timer = window.setTimeout(() => {
      if (previewGenerations.get(form) !== generation) return;
      previewTimers.delete(form);
      updateDraftPreview(root, generation);
    }, 160);
    previewTimers.set(form, timer);
  };

  // Close before competing bubbling handlers; native click also covers keyboard activation.
  document.addEventListener("click", (event) => {
    const close = event.target instanceof Element && event.target.closest("[data-guru-collection-filter-close]");
    if (!close) return;
    event.preventDefault();
    closeFilters(rootFor(close));
  }, true);

  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const root = rootFor(target);
    if (!root) return;

    const trigger = target.closest("[data-guru-collection-filter-trigger]");
    if (trigger) {
      event.preventDefault();
      const dialog = dialogFor(root);
      if (desktopQuery.matches && dialog?.open) closeFilters(root);
      else openFilters(root);
      return;
    }

    if (target.matches("[data-guru-collection-search-input]") && stickySearchIsAboveScrolledResults(root)) {
      scrollCollectionGridToStart(root);
    }

    const reset = target.closest("[data-guru-collection-filter-reset]");
    if (reset) {
      event.preventDefault();
      const form = formFor(root);
      form?.querySelectorAll('input[type="checkbox"]').forEach((input) => { input.checked = false; });
      form?.querySelectorAll('input[type="number"]').forEach((input) => { input.value = ""; });
      syncQuickFilters(root);
      queueDraftPreview(root);
      return;
    }

    const loadMore = target.closest("[data-guru-collection-load-more]");
    if (loadMore instanceof HTMLAnchorElement) {
      event.preventDefault();
      loadNextCollectionPage(root);
      return;
    }

    const quickFilter = target.closest("[data-guru-collection-quick-filter]");
    if (quickFilter) {
      event.preventDefault();
      if (quickFilter.disabled) return;
      const type = quickFilter.dataset.guruCollectionQuickFilter;
      const inputs = Array.from(root.querySelectorAll(`[data-guru-${type}-input]`));
      const shouldSelect = !inputs.some((input) => input.checked);
      inputs.forEach((input) => { if (!input.disabled || input.checked) input.checked = shouldSelect; });
      syncQuickFilters(root);
      if (desktopQuery.matches) {
        navigateCollection(root, urlFromForm(formFor(root)), {
          focusTarget: focusTargetFor(quickFilter),
          focusFallback: "filter",
          scrollToGrid: true,
        });
      } else {
        queueDraftPreview(root);
      }
      return;
    }

    const searchPage = target.closest("[data-guru-collection-search-page]");
    if (searchPage instanceof HTMLAnchorElement) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || searchPage.target === "_blank") return;
      event.preventDefault();
      navigateCollection(root, searchPage.href, { scrollToGrid: true, focusTarget: { kind: "grid" } });
      return;
    }

    const collectionLink = target.closest("[data-guru-collection-link], [data-guru-collection-pagination] a");
    if (collectionLink instanceof HTMLAnchorElement) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || collectionLink.target === "_blank") return;
      event.preventDefault();
      const destination = cleanUrl(collectionLink.href);
      const queryParam = localSearchParam(root);
      const currentQuery = localSearchQuery(root);
      if (currentQuery && !collectionLink.matches("[data-guru-clear-filters]") && !destination.searchParams.has(queryParam)) {
        destination.searchParams.set(queryParam, currentQuery);
      }
      const shouldScrollToGrid = Boolean(
        collectionLink.closest("[data-guru-collection-pagination]") ||
        collectionLink.matches("[data-guru-active-filter], [data-guru-clear-filters]")
      );
      navigateCollection(root, destination, {
        scrollToGrid: shouldScrollToGrid,
        focusTarget: collectionLink.closest("[data-guru-collection-pagination]") ? { kind: "grid" } : null,
        focusFallback: collectionLink.matches("[data-guru-active-filter], [data-guru-clear-filters]") ? "filter" : null,
      });
      return;
    }

    if (target.closest("[data-guru-collection-retry]") && failedSearch) {
      event.preventDefault();
      const retry = failedSearch;
      failedSearch = null;
      performCollectionSearch(root, retry.query, retry.options);
      return;
    }

    if (target.closest("[data-guru-collection-retry]") && failedNavigation) {
      event.preventDefault();
      const retry = failedNavigation;
      failedNavigation = null;
      replaceCollection(root, retry.url, retry.options);
    }
  });

  document.addEventListener("change", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const root = rootFor(target);
    if (!root) return;

    if (target.matches("[data-guru-collection-sort]")) {
      const url = new URL(window.location.href);
      if (target.value) url.searchParams.set("sort_by", target.value);
      else url.searchParams.delete("sort_by");
      url.searchParams.delete("page");
      updateSortValue(root);
      navigateCollection(root, url, {
        focusTarget: focusTargetFor(target),
        focusFallback: "sort",
        keepDesktopFilters: dialogFor(root)?.open,
        scrollToGrid: true,
      });
      return;
    }

    if (target instanceof HTMLInputElement && target.matches("[data-guru-panel-quick-control]")) {
      const type = target.dataset.guruPanelQuickControl;
      root.querySelectorAll(`[data-guru-${type}-input]`).forEach((input) => {
        if (!input.disabled || input.checked) input.checked = target.checked;
      });
      syncQuickFilters(root);
      queueDraftPreview(root);
      return;
    }

    if (target.closest("[data-guru-collection-filter-form]")) {
      syncQuickFilters(root);
      if (desktopQuery.matches) {
        navigateCollection(root, urlFromForm(formFor(root)), {
          keepDesktopFilters: true,
          focusTarget: focusTargetFor(target),
          focusFallback: "filter",
        });
      } else {
        queueDraftPreview(root);
      }
    }
  });

  document.addEventListener("input", (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches("[data-guru-collection-search-input]")) return;
    queueCollectionSearch(rootFor(input), { scrollToGrid: true });
  });

  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.matches("[data-guru-collection-sale-remove-form]")) {
      const root = rootFor(form);
      if (!root) return;
      event.preventDefault();
      navigateCollection(root, urlFromForm(form), { focusFallback: "filter", scrollToGrid: true });
      return;
    }

    if (form.matches("[data-guru-collection-search]")) {
      event.preventDefault();
      const root = rootFor(form);
      const input = form.querySelector("[data-guru-collection-search-input]");
      if (root && input) performCollectionSearch(root, input.value, { scrollToGrid: true });
      return;
    }

    if (!form.matches("[data-guru-collection-filter-form]")) return;
    const root = rootFor(form);
    if (!root) return;
    event.preventDefault();
    const apply = root.querySelector("[data-guru-collection-filter-apply]");
    if (apply?.disabled) return;
    closeFilters(root, { restoreFocus: false, resetDraft: false });
    navigateCollection(root, urlFromForm(form), { focusFallback: "filter", scrollToGrid: true });
  });

  document.addEventListener("cancel", (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.matches("[data-guru-collection-filter-dialog]")) return;
    event.preventDefault();
    closeFilters(rootFor(dialog));
  }, true);

  document.addEventListener("click", (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.matches("[data-guru-collection-filter-dialog]")) return;
    const root = rootFor(dialog);
    if (desktopQuery.matches && root?.dataset.guruCollectionFilterLayout === "sidebar") return;
    closeFilters(root);
  });

  desktopQuery.addEventListener("change", () => {
    document.querySelectorAll(rootSelector).forEach((root) => closeFilters(root, { restoreFocus: false }));
  });

  window.addEventListener("popstate", () => {
    const root = document.querySelector(rootSelector);
    if (root) navigateCollection(root, window.location.href, { updateHistory: false });
  });

  document.addEventListener("shopify:section:load", (event) => {
    const root = event.target.querySelector?.(rootSelector);
    if (root) {
      root.querySelectorAll("noscript").forEach((node) => node.remove());
      syncQuickFilters(root);
      resetDraftEvidence(root);
      updateSortValue(root);
      const input = root.querySelector("[data-guru-collection-search-input]");
      const query = localSearchQuery(root);
      if (input) input.value = query;
      if (query) queueCollectionSearch(root, { updateHistory: false, currentUrl: window.location.href });
      initVideos(root);
      enhanceInfiniteLoading(root);
    }
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = event.target.querySelector?.(rootSelector);
    if (root) {
      const form = formFor(root);
      const searchForm = root.querySelector("[data-guru-collection-search]");
      window.clearTimeout(searchTimers.get(searchForm));
      invalidateDraftPreview(root);
      searchRequests.get(searchForm)?.abort();
      searchTimers.delete(searchForm);
      searchRequests.delete(searchForm);
      searchPausedForDraft.delete(root);
      previewGenerations.delete(form);
      collectionRequest?.abort();
      cleanupInfiniteLoading(root);
      disposeVideos(event.target);
      document.body.classList.remove("guru-collection-filter-open");
    }
  });

  const initialize = () => {
    document.querySelectorAll(rootSelector).forEach((root) => {
      syncQuickFilters(root);
      resetDraftEvidence(root);
      updateSortValue(root);
      const input = root.querySelector("[data-guru-collection-search-input]");
      const query = localSearchQuery(root);
      if (input) input.value = query;
      if (query) queueCollectionSearch(root, { updateHistory: false, currentUrl: window.location.href });
      enhanceInfiniteLoading(root);
    });
    initVideos(document);
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initialize, { once: true });
  else initialize();

  window[namespace] = { initialize, replaceCollection };
})();
